import React, { useEffect } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { Container, Row, Col } from "react-grid-system";
import { Field, useFormState } from "react-final-form";
import dayjs from "dayjs";
import ReactGA from "react-ga";

import {
  Error,
  required,
  isEmail,
  composeValidators,
} from "../components/Validation";
import Summary from "../components/Summary";
import CreditCard from "../components/CreditCard";
import Fade from "../animate/Fade";
import PhoneNumberField from "../components/PhoneNumberField";

const Book = ({ dateRange }) => {
  const { values } = useFormState();
  const {
    pay_by,
    date_flexible,
    move_date,
    firstname,
    lastname,
    email,
    phone,
    paymenttoken,
  } = values;

  useEffect(() => {
    ReactGA.event({
      category: "Move Calculator",
      action: "Step",
      label: "Book",
    });
  }, []);

  return (
    <Container className="form-fields row">
      <Row>
        <div className="page-title">
          <h3>
            Reserve my truck and crew {date_flexible ? "between " : "on "}
            <strong>
              {date_flexible
                ? `${dayjs(dateRange.startDate).format("MMMM Do")} and ${dayjs(
                    dateRange.endDate
                  ).format("MMMM Do")}`
                : dayjs(move_date).format("dddd, [the] Do [of] MMMM")}
            </strong>
          </h3>
        </div>
        <Col sm={12}>
          <Summary />
        </Col>
        <Col sm={12}>
          <h4 className="book-label">Billing Information</h4>
        </Col>
        <Col sm={12} md={6} className="form-fields__block">
          <label htmlFor="billing_firstname">
            First Name
            <Field
              name="billing_firstname"
              initialValue={firstname}
              component="input"
              type="text"
              className="input"
              validate={required()}
            />
          </label>
          <Error name="billing_firstname" />
        </Col>
        <Col sm={12} md={6} className="form-fields__block">
          <label htmlFor="billing_lastname">
            Last Name
            <Field
              name="billing_lastname"
              initialValue={lastname}
              component="input"
              type="text"
              className="input"
              validate={required()}
            />
          </label>
          <Error name="billing_lastname" />
        </Col>
        <Col sm={12} md={6} className="form-fields__block">
          <label htmlFor="billing_email">
            Email Address
            <Field
              name="billing_email"
              initialValue={email}
              component="input"
              type="text"
              className="input"
              validate={composeValidators(
                required("Please provide an email address"),
                isEmail
              )}
            />
          </label>
          <Error name="billing_email" />
        </Col>
        <Col sm={12} md={6} className="form-fields__block">
          <label htmlFor="billing_phone">
            Phone Number
            <PhoneNumberField
              name="billing_phone"
              initialValue={phone}
              component="input"
              type="text"
              className="input"
              validate={required()}
            />
            <Error name="billing_phone" />
          </label>
        </Col>
        <Col sm={12} className="form-fields__block">
          <label htmlFor="address_1">
            Address
            <Field
              name="address_1"
              component="input"
              type="text"
              className="input"
              validate={required()}
            />
          </label>
          <Error name="address_1" />
        </Col>
        <Col sm={6} className="form-fields__block">
          <label htmlFor="city">
            City
            <Field
              name="city"
              component="input"
              type="text"
              className="input"
              validate={required()}
            />
          </label>
          <Error name="city" />
        </Col>
        <Col sm={6} className="">
          <label htmlFor="state">
            State
            <Field
              name="state"
              component="input"
              type="text"
              className="input"
              validate={required()}
            />
          </label>
          <Error name="state" />
        </Col>
        <div className="payment-block">
          <Row>
            <Col sm={12}>
              <h4 className="book-label">Payment:</h4>
              <label htmlFor="pay_by-check" className="radio">
                <Field
                  id="pay_by-check"
                  name="pay_by"
                  validate={required("Please select a payment option")}
                  component="input"
                  type="radio"
                  value="check"
                  disabled={!!paymenttoken}
                />{" "}
                Pay with a cashiers check or credit card when the truck unloads
              </label>
            </Col>
            <Col sm={12}>
              <label htmlFor="pay_by-card" className="radio">
                <Field
                  id="pay_by-card"
                  name="pay_by"
                  component="input"
                  type="radio"
                  value="card"
                  disabled={!!paymenttoken}
                />
                <div style={{ paddingRight: 15 }}>
                  Verify a credit card
                  <h6 className="book-subtitle">
                    This is needed to book a specific day. No funds will ever be
                    charged until your move is complete.
                  </h6>
                  <Fade show={pay_by === "card"}>
                    <Field
                      name="secured_funds"
                      validate={(value) =>
                        value
                          ? undefined
                          : "Funds have not been secured, please provide valid credit card details"
                      }
                    >
                      {({ input }) => (
                        <CreditCard
                          handleSuccess={(success) => {
                            input.onChange(success);
                          }}
                        />
                      )}
                    </Field>
                  </Fade>
                </div>
              </label>
              <Error name="pay_by" />
            </Col>
          </Row>
        </div>
      </Row>
    </Container>
  );
};

Book.propTypes = {
  dateRange: PropTypes.shape({
    startDate: PropTypes.instanceOf(Date),
    endDate: PropTypes.instanceOf(Date),
  }),
};

Book.defaultProps = {
  dateRange: null,
};

const mapStateToProps = (state) => ({
  dateRange: state.estimate.date_range,
});

export default connect(mapStateToProps, null)(Book);
